import { AlertTriangle, Wifi, WifiOff } from "lucide-react";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Badge } from "@/shared/components/ui/badge";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";

interface OfflineIndicatorProps {
  showOnlineStatus?: boolean;
  compact?: boolean;
}

export function OfflineIndicator({ showOnlineStatus = false, compact = false }: OfflineIndicatorProps) {
  const { isOnline, isReconnecting, lastOnlineAt } = useNetworkStatus();

  if (isOnline && !showOnlineStatus) {
    return null;
  }

  if (compact) {
    return (
      <Badge 
        variant={isOnline ? "default" : "destructive"} 
        className="flex items-center gap-1"
        data-testid={isOnline ? "status-online" : "status-offline"}
      >
        {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
        {isOnline ? "Online" : isReconnecting ? "Reconnecting..." : "Offline"}
      </Badge>
    );
  }

  if (!isOnline) {
    return (
      <Alert variant="destructive" className="border-orange-200 bg-orange-50" data-testid="offline-alert">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <strong>Offline Mode</strong> - You can still view cached data and submit reports. 
            Changes will sync when connection is restored.
          </div>
          {lastOnlineAt && (
            <div className="text-xs text-muted-foreground">
              Last online: {lastOnlineAt.toLocaleTimeString()}
            </div>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

export default OfflineIndicator;